/** @jsx jsx */
/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';
import { Component, createRef, Fragment, ReactNode } from 'react';
import Bugsnag from '@bugsnag/js';
import { Modal, Select, DatePicker, Checkbox, Skeleton } from 'antd';
import { Equipment } from '../interfaces/equipment';
import { Sensor } from '../interfaces/sensor';
import { get, set, size, reverse, sortBy, PropertyPath, flatten, unset } from 'lodash';
import { Publish } from '../interfaces/publish';
import { Chart, registerables } from 'chart.js';
// Chart.js v3+ requires a date adapter for time scales. We use moment since
// it's already loaded in this file.
import 'chartjs-adapter-moment';
import SharedStyles from '../styles';
import colors from '../colors';
import {
  convertHex,
  getEquipSensors,
  getEquipThresholds,
  singleOrAverage,
  equipIsMetric,
  metricConvert,
  isPB,
  getChartValues,
  getAvg,
  getAllDeviceIds,
  getEqType,
} from '../../utils';
import moment, { Moment } from 'moment';
// AER-1465: antd v6's DatePicker uses dayjs internally (not moment). Passing
// a moment instance as `value`/`defaultValue` causes the picker's internal
// view-date math to misinterpret the object on every render, producing the
// runaway "Jun 2092 → Jul 2102 → Jul 2440 → Jul 2862…" calendar cycling that
// QA flagged in DataVis_CustomDate.mp4. Convert moment ↔ dayjs at the
// DatePicker boundary so antd gets the type it expects while the rest of
// the component (and chart.js, which uses chartjs-adapter-moment) keeps
// using moment.
import dayjs from 'dayjs';
import styles from './modal-chart.styles';
import { connect } from 'react-redux';
import { AppState } from '../../app.state';
// AER-1465: `antd/lib/date-picker/interface` (antd v3's RangePickerValue
// type) was removed in antd v5+. The dateRangeChange handler now types
// _pickerVals as `any` since antd v6's RangePicker yields a Dayjs tuple.
import { trackEvent } from '../services/analytics.service';
import eventTypes from '../interfaces/api/eventTypes';
import { timeText } from '../../dashboard/equipment-details/equipment-details-columns';
import { AIcon as Icon } from '../icon';
import { getHistory } from '../services/device-history.service';
import { addToMap } from '../../dashboard/dashboard.actions';
import { distinct, productOf } from '../lib/device-events';
import { eventTime, newestFirst } from '../../utils/statistics';

Chart.register(...registerables);

interface IProps {
  sState: AppState;
  visible?: boolean;
  chosenSensor?: string;
  equipment?: Equipment;
  onClose?: () => void;
  loading: boolean;
  //tslint:disable-next-line:no-any
  latestSensorInfo: any;
  deviceId: string;
  alerts?: Publish[];
  isTime?: {};
  headerCols?: ICol[];
  addToMap: (arr: Publish[], opts: {}) => void;
  data?: {};
}

interface ICol {
  title: string;
  val: () => string;
  visible?: () => boolean;
}

interface IState {
  hasAll: boolean;
  sensorMap: {};
  chartDataModels: IDataset[];
  filterMap: { [key: string]: boolean };
  date: {
    selected: string;
    start: Moment;
    end: Moment;
  };
}

interface IDataset {
  label: string;
  //tslint:disable-next-line:no-any
  data: any;
  backgroundColor: string;
  pointBorderColor?: string;
  borderColor: string;
  borderWidth: number;
  hidden: boolean;
  show?: boolean;
}

interface _Point {
  x: Date | Moment;
  y: number;
  vals?: number[];
}

interface SensorItem {
  min: _Point[];
  max: _Point[];
  hmin: _Point[];
  hmax: _Point[];
  dailyAverage: _Point[];
  hourlyAverage: _Point[];
  total: _Point[];
  alertData: _Point[];
  instants: _Point[];
  threshHigh: _Point[];
  threshLow: _Point[];
}
type SensorMap = { [key: string]: SensorItem };

const createSensorMapItem = (): SensorItem => {
  return {
    min: [] as _Point[],
    max: [] as _Point[],
    hmin: [] as _Point[],
    hmax: [] as _Point[],
    total: [] as _Point[],
    dailyAverage: [] as _Point[],
    hourlyAverage: [] as _Point[],
    alertData: [] as _Point[],
    instants: [] as _Point[],
    threshHigh: [] as _Point[],
    threshLow: [] as _Point[],
  };
};

const dateoptions = [
  { title: '1 day', value: '1_day', days: 1 },
  { title: '1 week', value: '1_week', days: 7 },
  { title: '1 month', value: '1_month', days: 30 },
  { title: '3 months', value: '3_month', days: 90 },
  { title: '6 months', value: '6_month', days: 180 },
  { title: 'Custom', value: 'custom_date', days: Infinity },
];

const removeDupText = (t: any) => {
  return `${t}`.split('minsmins').join('mins').split('minmins').join('min');
}

// AER-1700: an Aeras Intel board publishes a JSON null for a reading whose probe
// is unreadable (e00fce6828d22cf062c22f94_0 / 5002015301 reported
// `AT: [null, null, null]` for ~22 hours straight). Dynamo can't hold a real
// null, so the publish is stored as the sentinel string 'null' and the server's
// fixEmpty() turns it back into a real null on the way out — which means
// `data.<sensor>` reaches us as `[null, null, null]`.
//
// These sentinels must never be treated as readings. `null < Infinity` is true
// (ToNumber(null) === 0), so a raw comparison happily latches a min/max onto
// null and the following `.toFixed()` then throws
// "Cannot read properties of null (reading 'toFixed')", which the app-level
// Bugsnag ErrorBoundary turns into a blank page.
const isMissingReading = (v: any) =>
  v === null ||
  v === undefined ||
  v === 'null' ||
  v === 'NULL' ||
  (typeof v === 'number' && !isFinite(v));

// Coerces a published reading to a finite number, or undefined when it isn't one
// (null sentinel, empty string, NaN, boolean, …). Only use where we do maths.
const numericReading = (v: any): number | undefined => {
  if (isMissingReading(v)) { return undefined; }
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && isFinite(n) ? n : undefined;
};

// AER-1758: a value is only safe to round if it is *already* a number. chart.js
// renders `formattedValue` through Intl.NumberFormat, so a four figure reading
// reaches a tooltip callback as the locale string "1,234.57" — and parseFloat
// stops dead at the group separator, returning 1. Rounding that would print a
// confident "1 min" for a 1235 minute session, which is worse than the ugly
// unrounded string it replaced. Formatted text never comes through here.
const finiteNumber = (v: any): number | undefined =>
  typeof v === 'number' && isFinite(v) ? v : undefined;

// AER-1758: the numbers chart.js hands a tooltip callback, most trustworthy
// first. `parsed.y` is the plotted value and is always present for the line and
// bar charts we draw; `raw` is the untouched data element we pushed ({ y, x }),
// so it still holds a real number when a future chart type leaves `parsed`
// empty. Anything non numeric is rejected rather than parsed — see finiteNumber.
const tooltipNumber = (item: any): number | undefined => {
  const nums = [
    get(item, 'parsed.y'),
    get(item, 'raw.y'),
    get(item, 'raw'),
  ].map(finiteNumber);

  return nums.filter((n) => n !== undefined)[0];
};

// AER-1758: 'mins' is the sensor record's own suffix, so a reading rounded to a
// single minute would read "1 mins". Drop the plural the same way timeText does
// on the cards. The tooltip and the y axis ticks both label themselves through
// here, so a hover can never disagree with the gridline it is sitting on.
const minuteUnit = (suffix: string, value?: number) =>
  suffix === 'mins' && value === 1 ? 'min' : suffix;

// AER-1700: the firmware families disagree about how a statistics publish
// labels itself. The DZ boards send the numeric enum (0 = daily, 1 = hourly,
// 2 = debug); the newer Aeras Intel boards send the strings 'daily' / 'hourly'.
//
// The model record that declares which value means "daily" is inconsistent in
// exactly the same way: every model in the table that sets `daily_type` today
// sets it to the string 'daily' (and `hourly_type` to 'hourly'), while the
// models that omit it fall back to the numeric defaults below. So BOTH sides of
// the comparison have to be normalised — normalising only the publish turns the
// working `'daily' === 'daily'` into `0 === 'daily'` and silently drops every
// record, which charts nothing for every unit. Values we don't recognise are
// passed through untouched so unrelated types still fail the comparison.
const statTypeNames: { [key: string]: number } = { daily: 0, hourly: 1, debug: 2 };
const normStatType = (raw: any) => {
  if (typeof raw !== 'string') { return raw; }
  const key = raw.trim().toLowerCase();
  //own-property check only: lodash `get` would happily resolve 'constructor'
  return Object.prototype.hasOwnProperty.call(statTypeNames, key) ? statTypeNames[key] : raw;
};
const statType = (s: Publish, key = 'data.statistics_type') => normStatType(get(s, key));

// AER-1700: the only ErrorBoundary in the app is the Bugsnag one at the root
// (app.component.tsx) and it has no fallback, so anything that throws while the
// chart modal renders unmounts the entire dashboard — the blank page QA recorded
// on Prod. Keep the blast radius inside the modal: still report to Bugsnag, but
// degrade to a message rather than taking the app down with us.
class ChartErrorBoundary extends Component<
  { children?: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    Bugsnag.notify(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div css={css(styles.boundaryMessage)}>
          This chart could not be displayed because of a problem with the data
          reported by this unit.
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}

// tslint:disable-next-line: no-any
let chart: any;
export class _ModalChart extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      hasAll: false,
      sensorMap: {},
      chartDataModels: [],
      filterMap: {},
      date: {
        selected: '1_month',
        start: moment().subtract(1, 'months'),
        end: moment(),
      },
    };
  }
  // tslint:disable-next-line: no-any
  chartRef: any = createRef();
  // AER-1465 (2026-05-14): use setState (not lodash `set` mutation) so the
  // date dropdown actually re-renders with the reset value when the modal
  // reopens for a different sensor. Combined with switching the Select to a
  // controlled `value` prop below, this keeps the dropdown label in sync
  // with the underlying date range across modal opens.
  resetDate = () => ({
    selected: '1_month',
    start: moment().subtract(1, 'months'),
    end: moment(),
  });
  close = () => {
    const { onClose } = this.props;
    this.setState({
      date: this.resetDate(),
      filterMap: {},
    });
    onClose && onClose();
  };

  getAllData = async () => {
    if (!this.state.hasAll) {
      const eq = this.props.equipment;
      const allDeviceIds = getAllDeviceIds(eq);

      const eventsToGet = [
        'dz_sensor_statistics',
        'ai_sensor_statistics',
      ];

      const allHistory = flatten(
        await Promise.all(
          allDeviceIds.map(
            async ids =>
              await Promise.all(
                eventsToGet.map(
                  async e => await getHistory(ids.id, e, ids.replaceDate, 5000)
                )
              )
          )
        )
      );

      const flat = flatten(allHistory).map(p => ({
        ...p,
        coreid: get(allDeviceIds, [0, 'id']),
      })) as Publish[];

      this.props.addToMap(flat, {
        clear: ['sensorStatistics'],
      });
    }
    this.setState({ hasAll: true, date: { ...this.state.date, ...get(this, 'props.date', {}) } });
  }
  componentDidUpdate = async (prevProps: IProps) => {
    // AER-1465 (2026-05-14): when the modal opens (or the user opens it for
    // a different sensor without closing first) reset the date dropdown back
    // to the default 1-month range. The chart itself already defaults to
    // 1 month of data on open; this keeps the dropdown label in sync.
    const justOpened = this.props.visible && !prevProps.visible;
    const sensorChangedWhileOpen =
      this.props.visible &&
      this.props.chosenSensor !== prevProps.chosenSensor;
    if (justOpened || sensorChangedWhileOpen) {
      this.setState({ date: this.resetDate(), filterMap: {} });
    }
    if (justOpened) {
      await this.getAllData();
      this.buildMap();
    }
    if (get(this, 'props.equipment.deviceId') !== get(prevProps, 'equipment.deviceId')) {
      this.setState({ hasAll: false });
    }
  };
  shouldComponentUpdate(nextProps: Readonly<IProps>, nextState: Readonly<IState>): boolean {
    return !![
      [this.props.visible, nextProps.visible],
      [get(this, 'props.equipment.deviceId'), get(nextProps, 'equipment.deviceId')],
      [this.state.sensorMap, nextState.sensorMap],
      [this.state.filterMap, nextState.filterMap],
      [this.state.date, nextState.date],
      [this.state.chartDataModels, nextState.chartDataModels]
    ].find(f => f[0] != f[1]);
  }

  // The product these publishes are from, which decides how each is read.
  product = () => productOf(getEqType(this.props.equipment), this.props.equipment);
  // Where a row goes on the chart: when what it describes happened (a daily on the day it covers,
  // other statistics at the end of their period), or where a caller placed a row it built for the
  // chart itself (chartAt). published_at is only when a row arrived.
  timeOf = (s: Publish): Moment =>
    get(s, 'chartAt') ? moment(get(s, 'chartAt')) : eventTime(this.product(), s);

  renderHeader = () => {
    const { chosenSensor } = this.props;
    const { sState, equipment } = this.props;
    const ispb = isPB(sState);
    const deviceId = get(equipment, 'deviceId');
    const sensor = this.getSensorFromSState();

    const isMetric = equipIsMetric(equipment, sState);

    //get all sensor statistics for the device
    const sensors = get(
      sState,
      `dash.equipMap.${deviceId}.sensorStatistics`,
      []
    );

    if (!Array.isArray(sensors) || sensors.length <= 0) return null;

    const daily_type = normStatType(get(this, 'props.daily_type', get(this, 'props.model.daily_type', 0)));

    //get the latest daily publish for the chosen sensor
    const daily = sensors.filter(
      (s: Publish) =>
        get(s, 'data.daily') || statType(s) === daily_type || statType(s, 'data.type') === daily_type
    );
    // the newest by the day it covers, not the last to arrive: a utility unit can send a day's
    // totals months late
    const latestDailySensorInfo = get(this, 'props.headerSensorInfo', newestFirst(this.product(), daily as Publish[])[0]);
    let latestDailyChosenSensor: any = get(
      latestDailySensorInfo,
      `data.${chosenSensor}`,
    );

    // only a daily for yesterday or today is current
    if (!!latestDailyChosenSensor && latestDailySensorInfo) {
      const current = this.timeOf(latestDailySensorInfo).isSameOrAfter(
        moment().subtract(1, 'day').startOf('day')
      );
      if (!current) {
        latestDailyChosenSensor = undefined;
      }
    }

    //some data gets saved this way because dynamodb can't handle regular nulls.
    //AER-1700: a daily publish taken while the probe was dead reads
    //[null, null, null], which is truthy but holds no readings — treat it as
    //missing so we fall back to the 24 hour scan below instead of showing N/A.
    const dailyIsEmpty =
      Array.isArray(latestDailyChosenSensor) &&
      latestDailyChosenSensor.every(isMissingReading);

    if (
      latestDailyChosenSensor === 'null' ||
      !latestDailyChosenSensor ||
      dailyIsEmpty
    ) {
      const now = moment();
      const obj = {
        min: Infinity,
        max: -Infinity,
        avg: [] as number[],
      };
      sensors
        .filter((s: Publish) => now.diff(this.timeOf(s), 'hours') <= 24)
        .map((s: Publish) => {
          const d = get(s, `data.${chosenSensor}`);

          //AER-1700: coerce before comparing. Anything that isn't a finite
          //number (the null sentinel above all) is skipped rather than allowed
          //to become the running min/max — that is what used to throw on
          //`.toFixed()` below and blank the whole dashboard.
          const vals = Array.isArray(d) ? d : [d, d, d];
          const min = numericReading(get(vals, 0));
          const max = numericReading(get(vals, 1));
          const avg = numericReading(get(vals, 2));

          if (min !== undefined && min < obj.min) {
            obj.min = min;
          }
          if (max !== undefined && max > obj.max) {
            obj.max = max;
          }
          if (avg !== undefined) {
            obj.avg.push(avg);
          }
        });

      const avgOfDay = getAvg(obj.avg);

      latestDailyChosenSensor = [
        isFinite(obj.min) ? obj.min.toFixed(1) : undefined,
        isFinite(obj.max) ? obj.max.toFixed(1) : undefined,
        isFinite(avgOfDay) ? avgOfDay.toFixed(1) : undefined,
      ];
    }

    //Latest instant data from equip-details
    const lastInstant = get(this.props, 'latestSensorInfo', null);

    //make sure data is there
    const valueText = (
      val: string | number | undefined | null,
      ifVal: string,
      ifNull = undefined
    ) => {
      if (val === '--' || (val || '').toString().includes('/')) {
        return val;
      }
      const v = singleOrAverage(val);

      if (this.props.isTime) {
        return timeText(v, this.props.isTime);
      }

      return typeof v !== 'boolean' && (v === null || isNaN(v))
        ? ifNull
        : ifVal;
    };

    const na = 'N/A';

    let cols = get(this, 'props.headerCols', [
      {
        title: (() => get(this, 'props.currentReadingTitle', 'Current Reading'))(),
        val: () => {
          const val = valueText(lastInstant, `${singleOrAverage(lastInstant)}`);
          if (chosenSensor === 'AMG') {
            return val === '0' ? 'Working' : val === '1' ? 'Full' : na;
          }
          return val ? this.getSensorSuffix(val) : na;
        },
      },
      {
        title: 'Min value within last 24 hours',
        visible: () => get(sensor, 'showMin', true),
        val: () => {
          const val = valueText(
            get(latestDailyChosenSensor, 0, null),
            `${singleOrAverage(get(latestDailyChosenSensor, 0, null))}`
          );
          return val ? this.getSensorSuffix(val) : na;
        },
      },
      {
        title: 'Max value within last 24 hours',
        visible: () => get(sensor, 'showMax', true),
        val: () => {
          const val = valueText(
            get(latestDailyChosenSensor, [1], null),
            `${singleOrAverage(get(latestDailyChosenSensor, [1], null))}`
          );
          return val ? this.getSensorSuffix(val) : na;
        },
      },
      //@ts-ignore
    ]).filter(f => !!get(f, 'visible', () => true)()) as ICol[];

    if (!size(cols)) { return null }

    return (
      <div css={css(SharedStyles.row, styles.headerWrap)}>
        {cols.map((c, i) => {
          return (
            <div
              key={i}
              css={css(
                ispb ? styles.pbHeaderCol : styles.headerCol,
                `width: ${100 / cols.length}%;`
              )}
            >
              <div css={css(ispb && `padding: 0 3px; text-align: center;`)}>
                {c.title}
              </div>
              <div css={css(styles.headerColVal)}>
                {metricConvert(removeDupText(c.val()), isMetric) as any}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  getSensorFromMap = () => {
    const { chosenSensor } = this.props;
    return get(this.state.sensorMap, chosenSensor || '', createSensorMapItem());
  };
  getSensorFromSState = () => {
    const { sState, chosenSensor } = this.props;

    const sensor = get(sState, `dash.sensors`, []).find(
      (s: Sensor) => s.id === chosenSensor
    );

    return {
      ...(sensor || {}),
      ...get(this, 'props.sensorOverrides', {}),
    };
  };
  getSensorSuffix = (v?: any) => {
    const sensor = this.getSensorFromSState();

    const suff = get(sensor, 'suffix', '');

    if (!v) { return suff }

    //AER-1700: callers hand us whatever `valueText` returned, which is not
    //always a string (a raw reading is passed straight through when it is '--'
    //or contains a '/'). Normalise so `.includes` / `.toLowerCase` can't throw.
    const val = typeof v === 'string' ? v : `${v}`;

    //this is because most of the suffix setup in sensors we want a space between the value and the suffix
    //however you should be able to include a space in the sensor suffix just by changing it in the db
    const isPercent = suff === '%';

    return val.includes(suff) || val.toLowerCase() == 'n/a' ? val : val + `${isPercent ? '' : ' '}${suff}`;
  };
  legendItemToggled = (item: IDataset) => {
    let { filterMap } = this.state;

    //if item isn't in the map yet, add it
    //otherwise invert it in place
    if (filterMap[item.label] != undefined) {
      filterMap[item.label] = !filterMap[item.label];
    } else {
      filterMap[item.label] = !item.hidden;
    }

    this.setState({ filterMap }, this.renderChart);
  };
  renderLegend = (): JSX.Element => {
    const dataSets = this.state.chartDataModels;
    const { sState } = this.props;
    const ispb = isPB(sState);
    if (!dataSets) return <></>;

    const createItem = (i: IDataset, index: number): JSX.Element => {
      // antd v6's DOM no longer has `.ant-checkbox-inner` — the visible box IS
      // the `.ant-checkbox` span itself, painted by `@ant-design/cssinjs` with
      // `token.colorPrimary` (Aeras' brand purple) on `.ant-checkbox-checked`.
      // We override per-dataset colors here. cssinjs doesn't emit !important,
      // so a single !important on our rule wins regardless of stylesheet order.
      const checkboxColorCss = css`
        .ant-checkbox.ant-checkbox-checked {
          background-color: ${i.borderColor} !important;
          border-color: ${i.borderColor} !important;
        }
        .ant-checkbox-wrapper:hover
          .ant-checkbox.ant-checkbox-checked:not(.ant-checkbox-disabled) {
          background-color: ${i.borderColor} !important;
          border-color: ${i.borderColor} !important;
        }
      `;

      return (
        <div
          css={[
            checkboxColorCss,
            ispb &&
              css`
                width: 50%;
                padding: 3px 0;
              `,
          ]}
          key={index}
        >
          <Checkbox
            checked={get(i, 'show', !i.hidden)}
            onChange={_ => this.legendItemToggled(i)}
          >
            {i.label}
          </Checkbox>
        </div>
      );
    };

    return (
      <div css={css([styles.legend, ispb && `flex-wrap: wrap`])}>
        {dataSets.map((item, index) => createItem(item, index))}
      </div>
    );
  };
  isDataSetHidden = (label: string, fallback: boolean): boolean => {
    const { filterMap } = this.state;
    return !!get(filterMap, label, fallback);
  };
  renderChart = async () => {
    if (!this.chartRef.current) return;
    const myChartRef = this.chartRef.current.getContext('2d');
    if (chart !== undefined) chart.destroy();

    const { chosenSensor, equipment, sState, data } = this.props;
    const ispb = isPB(sState);
    const { filterMap, date } = this.state;
    const sensorSelect = this.getSensorFromMap();
    const suffix = this.getSensorSuffix();
    const sensor = this.getSensorFromSState();

    // AER-1758: a chart plots minutes either because the sensor record says so
    // (a 'mins' suffix, which also labels the y axis) or because the caller
    // handed us a seconds -> minutes transform via `isTime`. That covers every
    // session / consultation / treatment / turnover / elapsed chart we have,
    // across chairs, handpieces and sterilizers, and leaves the PSI / F / % /
    // hrs sensors — where decimals are meaningful — alone.
    const isMinuteChart =
      suffix === 'mins' || !!get(this, 'props.isTime.transformValue');

    const datasetProps = {
      showAlerts: true,
      showMin: true,
      showMax: true,
      showHourly: true,
      showDaily: true,
      showTotal: !!size(sensorSelect.total),
      label_daily: 'Daily Average',
      label_min: 'Min Values',
      label_max: 'Max Values',
      label_alert: 'Alert Data',
      label_hourly: 'Hourly Average',
      label_total: 'Daily Total',
      ...(sensor || {}),
    };

    const isMetric = equipIsMetric(equipment, sState);

    const datasets: IDataset[] = [];

    if (chosenSensor !== 'bars' && datasetProps.showAlerts) {
      const label = datasetProps.label_alert;
      datasets.push({
        label: label,
        data: reverse(sensorSelect.alertData),
        backgroundColor: 'transparent',
        borderColor: convertHex(colors.alert, 0.8),
        borderWidth: 1,
        hidden: this.isDataSetHidden(label, false),
      });
    }

    const showHourly =
      datasetProps.showHourly &&
      get(dateoptions.find(d => d.value == date.selected), 'days', Infinity) <=
      7;
    const includeHourly =
      showHourly && !this.isDataSetHidden('Hourly Average', false);

    if (chosenSensor !== 'AMG') {
      const dset = includeHourly
        ? sortBy(
          [...sensorSelect.dailyAverage, ...sensorSelect.hourlyAverage],
          'x'
        )
        : sensorSelect.dailyAverage;

      if (datasetProps.showDaily) {
        datasets.push({
          label: datasetProps.label_daily,
          data: reverse(dset),
          backgroundColor: get(this, 'props.pointBackgroundColor', 'transparent'),
          pointBorderColor: convertHex('#000000', 0.8),
          borderColor: convertHex('#000000', 0.8),
          borderWidth: 1,
          hidden: this.isDataSetHidden(datasetProps.label_daily, false),
        });
      }

      if (showHourly) {
        datasets.push({
          label: datasetProps.label_hourly,
          data: [], // reverse(sensorSelect.hourlyAverage),
          backgroundColor: 'transparent',
          pointBorderColor: convertHex('#000000', 0.8),
          borderColor: convertHex('#000000', 0.8),
          borderWidth: 1,
          hidden: this.isDataSetHidden(datasetProps.label_hourly, false),
        });
      }
    }

    if (datasetProps.showTotal) {
      const totalLabel = datasetProps.label_total;
      datasets.push({
        label: totalLabel,
        data: reverse(sensorSelect.total),
        backgroundColor: 'transparent',
        pointBorderColor: convertHex(colors.alert, 1),
        borderColor: convertHex(colors.alert, 1),
        borderWidth: 1,
        hidden: this.isDataSetHidden(totalLabel, true),
      });
    }

    if (datasetProps.showMin) {
      const minLabel = datasetProps.label_min;
      datasets.push({
        label: minLabel,
        data: reverse(
          includeHourly
            ? sortBy([...sensorSelect.min, ...sensorSelect.hmin], 'x')
            : sensorSelect.min
        ),
        backgroundColor: 'transparent',
        pointBorderColor: convertHex(colors.highlight, 1),
        borderColor: convertHex(colors.highlight, 1),
        borderWidth: 1,
        hidden: this.isDataSetHidden(minLabel, true),
      });
    }

    if (datasetProps.showMax) {
      const maxLabel = datasetProps.label_max;
      datasets.push({
        label: maxLabel,
        data: reverse(
          includeHourly
            ? sortBy([...sensorSelect.max, ...sensorSelect.hmax], 'x')
            : sensorSelect.max
        ),
        backgroundColor: 'transparent',
        pointBorderColor: convertHex(colors.warning, 1),
        borderColor: convertHex(colors.warning, 1),
        borderWidth: 1,
        hidden: this.isDataSetHidden(maxLabel, true),
      });
    }

    this.setState({ chartDataModels: datasets });

    // Chart.js v3/v4 options structure: plugins.legend, plugins.tooltip,
    // scales.x/y (objects, not xAxes/yAxes arrays), and `showLine` on the
    // dataset (not `showLines` at the chart root).
    const showLine = chosenSensor !== 'AMG';
    datasets.forEach(d => {
      (d as any).showLine = showLine;
      // line tension carried over so the curves render the same way as
      // chart.js v2 default of line tension 0
      (d as any).tension = 0;
    });

    const chartOpts = {
      type: get(this, 'props.chartType', 'line'),
      data: {
        datasets,
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              //@ts-ignore
              label: (tooltipItem: any) => {
                // AER-1758: minute readings reach the chart as seconds / 60
                // (or pre-divided by the caller), so the hover used to read
                // "33.416666666666664 mins". The y axis is already labelled in
                // whole minutes, so round the hover to the nearest one too.
                //
                // Only a genuine number is rounded. When chart.js gives us
                // nothing but its own locale formatted `formattedValue` we print
                // that string untouched: re-parsing it would turn "1,234.57"
                // into 1 and state the wrong number with total confidence.
                const num = tooltipNumber(tooltipItem);
                const rounded =
                  isMinuteChart && num !== undefined ? Math.round(num) : undefined;
                const valStr =
                  rounded !== undefined
                    ? `${rounded}`
                    : num !== undefined
                      ? `${num}`
                      : `${get(tooltipItem, 'formattedValue', get(tooltipItem, 'value', ''))}`;
                const unit = minuteUnit(suffix, rounded);
                return chosenSensor === 'AMG' && valStr === '100'
                  ? 'Full'
                  : chosenSensor === 'AMG' && valStr === '0'
                    ? 'Working'
                    : (metricConvert(
                      `${valStr}${suffix == 'mins' ? ` ${unit}` : suffix}`,
                      isMetric
                    ) as string);
              },
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            distribution: 'linear',
            time: {
              unit: 'day',
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 20,
            },
          },
          y: {
            ticks: {
              //@ts-ignore
              callback: (value: any) => {
                return chosenSensor === 'AMG' && value === 100
                  ? 'Full'
                  : chosenSensor === 'AMG' && value === 0
                    ? 'Working'
                    : chosenSensor === 'AMG'
                      ? ''
                      // AER-1758: the hover rounds to whole minutes and drops the
                      // plural at 1, so the axis beside it has to read the same way
                      // — otherwise a "1 min" tooltip sits on a "1 mins" gridline.
                      // `metricConvert` has nothing to convert here (a bare number
                      // carries no unit letter and is returned as-is) and a tick is
                      // never the 'n/a' text getSensorSuffix guards against, so this
                      // branch is that helper plus the singular.
                      : isMinuteChart && suffix === 'mins'
                        ? `${value} ${minuteUnit(suffix, value)}`
                        : (this.getSensorSuffix(metricConvert(`${value}`, isMetric)) as string);
              },
              autoSkip: true,
              maxTicksLimit: ispb ? 6 : 12,
              // AER-1758: "Today's Sessions" auto-scales to half minute ticks, so
              // the tallest bar sat on the "5.5 mins" gridline while its rounded
              // hover read "6 mins" — above every label on the axis. Asking
              // chart.js for whole number ticks fixes the mismatch at the source:
              // v4 rounds the spacing it picked up to this many decimals
              // (`spacing = Math.ceil(spacing * 10 ** precision) / 10 ** precision`)
              // and then emits every tick at that precision, so a minute axis can
              // only land on whole minutes. Rounding the tick *labels* instead
              // would print the same label on two neighbouring gridlines.
              // `undefined` is what chart.js already sees when the option is
              // omitted, so every other sensor keeps its decimals.
              precision: isMinuteChart ? 0 : undefined,
              stepSize:
                chosenSensor === 'AMG'
                  ? 100
                  : chosenSensor === 'bars'
                    ? 1
                    : undefined,
            },
            beginAtZero: true,
            max:
              chosenSensor === 'AMG'
                ? 200
                : chosenSensor === 'bars'
                  ? 5
                  : undefined,
          },
        },
      },
    };

    //@ts-ignore
    (get(this, 'props.chartOpts', []) as any[]).map((c: any) => {
      const [key, val] = c;
      if (val === undefined) {
        unset(chartOpts, key);
      } else {
        set(chartOpts, key, val);
      }
    })

    //@ts-ignore
    chart = new Chart(myChartRef, chartOpts);
  };
  dateChange = (val: string) => {
    trackEvent(eventTypes.eq_sensor_history_timeframe, {
      timeframe: val,
      equipment: this.props.equipment,
    });
    const state = { ...this.state };
    if (val === '1_day') {
      state.date.start = moment().subtract(1, 'day');
    } else if (val === '1_week') {
      state.date.start = moment().subtract(1, 'week');
    } else if (val === '1_month') {
      state.date.start = moment().subtract(1, 'month');
    } else if (val === '3_month') {
      state.date.start = moment().subtract(3, 'month');
    } else if (val === '6_month') {
      state.date.start = moment().subtract(6, 'month');
    }
    state.date.end = moment();
    state.date.selected = val;
    if (this.props.chosenSensor === 'AMG') {
      this.setState(state, this.buildMapForAMG);
    } else {
      this.setState(state, this.buildMap);
    }
  };
  dateRangeChange = (
    // antd v6 RangePicker onChange yields ([Dayjs, Dayjs] | null, [str, str]).
    // We accept the dayjs values but convert back to moment for state so
    // downstream chart code (which uses moment) keeps working unchanged.
    // tslint:disable-next-line: no-any
    _pickerVals: any,
    dates: [string, string]
  ) => {
    const state = { ...this.state };
    state.date.start = moment(dates[0]);
    state.date.end = moment(dates[1]);
    if (this.props.chosenSensor === 'AMG') {
      this.setState(state, this.buildMapForAMG);
    } else {
      this.setState(state, this.buildMap);
    }
  };
  // tslint:disable-next-line: no-any
  datePickerChange = (date: any, dateStr?: string | string[]) => {
    const state = { ...this.state };
    // antd v6 passes a Dayjs object; coerce via the formatted string so we
    // don't depend on moment vs dayjs identity.
    const ds = Array.isArray(dateStr) ? dateStr[0] : dateStr;
    const m = ds ? moment(ds) : moment(date && (date as any).toDate ? (date as any).toDate() : date);
    state.date.start = m.startOf('day');
    state.date.end = m.clone().endOf('day');
    if (this.props.chosenSensor === 'AMG') {
      this.setState(state, this.buildMapForAMG);
    } else {
      this.setState(state, this.buildMap);
    }
  };
  renderDateDrop = () => {
    const {
      date: { selected, start, end },
    } = this.state;

    const showDateSelect = get(this, 'props.showDateSelect', true);

    if (!showDateSelect) { return null; }

    return (
      <div>
        {/*
          AER-1465 (2026-05-14): use a controlled `value` (not `defaultValue`)
          so the dropdown label resets when the modal is reopened for a
          different sensor. Under antd v6 Select, `defaultValue` is only
          honored on first mount and the parent keeps this component mounted
          across modal opens, which left the previous selection visible even
          though the underlying date range had been reset to 1 month.
        */}
        <Select onChange={this.dateChange} value={selected}>
          {dateoptions.map((opt, i) => (
            <Select.Option value={opt.value} key={i}>
              {opt.title}
            </Select.Option>
          ))}
        </Select>
        {selected === 'custom_date' && (
          <DatePicker.RangePicker
            onChange={this.dateRangeChange as any}
            // AER-1465: convert moment → dayjs at the picker boundary. antd
            // v6 expects Dayjs instances; passing a Moment confuses the
            // internal "current view" calculation and the calendar header
            // races into the future on every re-render. Controlled `value`
            // (not defaultValue) keeps the picker in sync with state resets.
            value={[
              dayjs(start.toISOString()),
              dayjs(end.toISOString()),
            ] as any}
          />
        )}
        {selected === '1_day' && (
          <DatePicker
            onChange={this.datePickerChange as any}
            value={dayjs(start.toISOString()) as any}
          />
        )}
      </div>
    );
  };
  buildMapForAMG = () => {
    const { date } = this.state;
    const alerts: Publish[] = get(this.props, 'alerts', []);
    const sensorMap: SensorMap = {};
    alerts.map((alert: Publish, i: number) => {
      const data = alert.data;
      const start = moment(get(data, 'time', 1) * 1000);
      const canDisplay =
        start.isBetween(date.start, date.end) &&
        data.source === 'AMG' &&
        data.error === 2;
      if (canDisplay) {
        const error = data.error;
        const val = error === 2 ? 100 : 0;
        let sm = sensorMap['AMG'];
        if (!sm) {
          sm = sensorMap['AMG'] = createSensorMapItem();
        }

        // where it is filtered: when the alert happened
        const x = start;
        sm.alertData.push({ y: val, x });
      }
    });
    this.setState({ sensorMap }, this.renderChart);
  };
  cleanVal = (val?: number) => {
    const { sState, equipment, isTime } = this.props;
    const suffix = this.getSensorSuffix();
    const isMetric = equipIsMetric(equipment, sState);

    let v = val == undefined ? 0 : val;

    if (isMetric) {
      const { value } = metricConvert(`${val}${suffix}`, isMetric, true) as {
        value: string;
      };

      v = parseFloat(value);
    }

    if (isTime && get(isTime, 'transformValue')) {
      v = (get(isTime, 'transformValue') as any)(v);
    }

    return v;
  };
  buildMap = () => {
    if (this.props.chosenSensor === 'AMG') {
      return this.buildMapForAMG();
    }
    const { date } = this.state;
    const { sState, equipment } = this.props;
    const sensor = this.getSensorFromSState();

    // each event once, however often it arrived, newest first by when it happened; rows a caller
    // built for the chart keep their order
    const product = this.product();
    const callerData = get(this, 'props.data');
    const sensors = callerData
      ? distinct(product, callerData as Publish[])
      : newestFirst(product, (getEquipSensors(equipment, sState, []) || []) as Publish[]);
    const thresholds = newestFirst(product, (getEquipThresholds(equipment, sState, []) || []) as Publish[]);

    const chosenSensor = get(this, 'props.chosenSensor');

    const sensorMap: SensorMap = {};

    const all_ss = sensors
      .filter((s: Publish) => get(s, 'event', '') === 'dz_sensor_statistics')
    // .sort(sortPublishByTimeOrDate);

    // statistics_type: 0 = daily, 1 = hourly/session — or the equivalent
    // 'daily' / 'hourly' strings, hence normStatType on both sides (AER-1700).
    const daily_type = normStatType(get(this, 'props.daily_type', get(this, 'props.model.daily_type', 0)));
    const hourly_type = normStatType(get(this, 'props.hourly_type', get(this, 'props.model.hourly_type', 1)));
    //@ts-ignore
    const ss = all_ss.filter((s: Publish) => this.props.filterBy ? this.props.filterBy(s) :
      get(sensor, 'filterBy')
        ? !!get(s, (sensor as any).filterBy)
        : statType(s) === daily_type || statType(s, 'data.type') === daily_type
    );

    const hourly_ss = all_ss.filter(
      (s: Publish) => statType(s) === hourly_type
    );

    const si = sensors.filter((s: Publish) => s.event === 'dz_sensor_instant');
    const sensorAlerts = sensors.filter(
      (s: Publish) =>
        s.event === 'dz_alert_data' && get(s, 'data.source') === chosenSensor
    );

    ss.map((s: Publish) => {
      const data = s.data || {};
      for (let k in data) {
        let sm = sensorMap[k];
        if (!sm) {
          sm = sensorMap[k] = createSensorMapItem();
        }
        const { min, max, avg, total, has } = getChartValues(data[k]);
        const val = this.cleanVal(avg);
        const minVal = this.cleanVal(min);
        const maxVal = this.cleanVal(max);
        const totalVal = this.cleanVal(total);

        const time = this.timeOf(s);

        const canDisplay = time.isBetween(date.start, date.end);

        if (canDisplay) {
          //AER-1700: `cleanVal` maps a missing reading onto 0, which would plot
          //a fake dip to zero for every hour the probe was unreadable. Leave
          //those points out of the series instead.
          if (!isMissingReading(min)) { sm.min.push({ y: minVal, x: time }); }
          if (!isMissingReading(max)) { sm.max.push({ y: maxVal, x: time }); }
          if (!isMissingReading(avg)) {
            sm.dailyAverage.push({ y: val, x: time, vals: data[k] });
          }
          if (has('total') && !isMissingReading(total)) {
            //@ts-ignore
            sm.total.push({ y: totalVal, x: time });
          }
        }
      }
    });

    hourly_ss.map((s: Publish) => {
      const data = s.data || {};
      for (let k in data) {
        let sm = sensorMap[k];
        if (!sm) {
          sm = sensorMap[k] = createSensorMapItem();
        }
        const vals = getChartValues(data[k]);
        const val = this.cleanVal(vals.avg);
        const minVal = this.cleanVal(vals.min);
        const maxVal = this.cleanVal(vals.max);

        const time = this.timeOf(s);

        const canDisplay = time.isBetween(date.start, date.end);

        if (canDisplay) {
          //AER-1700: same as the daily series above — skip the hours the probe
          //reported nothing rather than charting them as 0.
          if (!isMissingReading(vals.min)) { sm.hmin.push({ y: minVal, x: time }); }
          if (!isMissingReading(vals.max)) { sm.hmax.push({ y: maxVal, x: time }); }
          if (!isMissingReading(vals.avg)) {
            sm.hourlyAverage.push({ y: val, x: time, vals: data[k] });
          }
        }
      }
    });

    si.map((s: Publish) => {
      const data = s.data || {};
      for (let k in data) {
        let sm = sensorMap[k];
        if (!sm) {
          sm = sensorMap[k] = createSensorMapItem();
        }
        const vals = data[k];
        const val = this.cleanVal(vals);

        const time = this.timeOf(s);

        sm.instants.push({ y: val, x: time, vals });
      }
    });

    sensorAlerts.map((s: Publish) => {
      const data = s.data || {};
      const start = moment(get(data, 'start_time', 1) * 1000);
      const interval = get(data, 'interval', 15);

      const canDisplay = start.isBetween(date.start, date.end);
      if (canDisplay) {
        (data.values || []).map((val: number, i: number) => {
          let sm = (sensorMap as any)[chosenSensor];
          if (!sm) {
            sm = (sensorMap as any)[chosenSensor] = createSensorMapItem();
          }

          const x = moment(start).add(interval * i, 'seconds');
          sm.alertData.push({ y: this.cleanVal(val), x });
        });
      }
    });

    thresholds.map((s: Publish) => {
      const data = s.data || {};
      for (let k in data) {
        let sm = sensorMap[k];
        if (!sm) {
          sm = sensorMap[k] = createSensorMapItem();
        }

        const vals = data[k] || [];
        const high = get(vals, '0');
        const low = vals.length > 2 ? get(vals, '2') : get(vals, '1');

        const time = this.timeOf(s);

        high !== undefined &&
          sm.threshHigh.push({ y: this.cleanVal(high), x: time });
        low !== undefined &&
          sm.threshLow.push({ y: this.cleanVal(low), x: time });
      }
    });

    const filterMap = this.state.filterMap;
    if (get(sensor, 'legendsSelected')) {
      get(sensor, 'legendsSelected', []).map((s: PropertyPath) => {
        set(filterMap, s, false);
      });
    }

    this.setState({ sensorMap, filterMap }, this.renderChart);
  };
  render() {
    const { visible, loading, sState } = this.props;
    const sensor = this.getSensorFromSState();
    const ispb = isPB(sState);

    const showLegend = get(this, 'props.showLegend', get(sensor, 'showLegend', true));

    return (
      <Modal
        open={visible}
        onCancel={this.close}
        closable={true}
        footer={null}
        width={ispb ? `90%` : `60%`}
        title={
          <div>
            <div css={css(styles.modalTitle)}>
              {get(this, 'props.title', get(sensor, 'name', 'None'))}
              {loading && (
                <Icon css={css('margin-left: 5px')} type={'loading'} />
              )}
            </div>
            <div css={css(styles.modalSubTitle)}>
              {get(this, 'props.description', get(sensor, 'description', ''))}
            </div>
          </div>
        }
      >
        {visible && !this.state.hasAll && (
          <div>
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </div>
        )}
        {visible && !!this.state.hasAll && (
          // AER-1700: the `key` remounts the boundary whenever the modal is
          // pointed at another unit or sensor, so a single bad reading doesn't
          // leave the modal permanently stuck on the fallback message.
          <ChartErrorBoundary
            key={`${get(this, 'props.equipment.deviceId', '')}-${this.props
              .chosenSensor || ''}`}
          >
            <div>
              {this.renderHeader()}
              {showLegend && this.renderLegend()}
              <canvas ref={this.chartRef} />
              {this.renderDateDrop()}
            </div>
          </ChartErrorBoundary>
        )}
      </Modal>
    );
  }
}

export const ModalChart = connect((sState: AppState) => ({ sState }), { addToMap })(
  _ModalChart as any
) as unknown as typeof _ModalChart;
